import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import constant from '../../constant';
import DoctorForm from '../ui/doctorForm';

const AddDoctor = () => {
  const navigate = useNavigate();
  const { hospitalId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hospitals, setHospitals] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    is_active: true,
  });

  useEffect(() => {
    const fetchHospitals = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          return;
        }

        const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const result = await response.json();
        if (response.ok) {
          setHospitals(result);
        } else {
          setError(result.message || 'Failed to fetch hospitals');
        }
      } catch (error) {
        setError('Error fetching hospitals');
      }
    };

    fetchHospitals();
  }, []);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const apiData = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobile,
        is_active: formData.is_active,
      };

      const response = await fetch(`${constant.REACT_APP_API_URL}/hospital/${hospitalId}/doctor/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to add doctor. Status: ${response.status}`);
      }

      setSuccessMessage('Doctor added successfully!');
      setFormData({
        name: '',
        email: '',
        mobile: '',
        is_active: true,
      });
      navigate(-1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 pt-20 md:pt-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-4">
            <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700 transition-colors">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Add Doctor</h2>
          </div>
        </div>

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4" role="alert">
            <strong className="font-bold">Success!</strong>
            <span className="block sm:inline ml-2">{successMessage}</span>
          </div>
        )}

        <DoctorForm
          initialValues={formData}
          hospitals={hospitals}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isEditMode={false}
        />
      </div>
    </div>
  );
};

export default AddDoctor;